import React, { useEffect, useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Zap, Shield, Bot, BarChart3, Users, Smartphone, CheckCircle2, ChevronDown, ChevronUp, Play, Sparkles, Linkedin, Mail, Phone, Clock, TrendingUp, Menu, X, Loader2, Globe, Building2, ShoppingBag, Tag, HelpCircle, Package, MessageCircle, Power, Link2, Database, Languages, ArrowRight, ArrowLeft, Send, Settings } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { dbClientBrands } from '../utils/supabase';
import { BrandsMarquee } from '../components/ui/BrandsMarquee';
type BillingPeriod = 'monthly' | 'yearly';
interface PricingPlan {
  name: string;
  nameAr: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  featuresAr: string[];
  popular?: boolean;
}
export function NewHomePage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const {
    country,
    setCountry,
    currency,
    currencySymbol,
    phoneNumber,
    pricing
  } = useCountry();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [clientBrands, setClientBrands] = useState<string[]>([]);
  // Fetch client brands for marquee
  useEffect(() => {
    dbClientBrands.getAll().then(brands => {
      const names = brands.map(b => b.brand_name).filter(Boolean);
      if (names.length > 0) setClientBrands(names);
    }).catch(() => {});
  }, []);
  // Handle country change with page refresh
  const handleCountryChange = (newCountry: 'eg' | 'ae' | 'sa') => {
    setCountry(newCountry);
    setCountryDropdownOpen(false);
    window.location.reload();
  };
  // Handle language change with page refresh
  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
    setLanguageDropdownOpen(false);
    window.location.reload();
  };
  // Detect device language on mount
  useEffect(() => {
    // Placeholder for language detection logic
  }, []);
  // Clients List (Mock data for now, would come from settings in real app)
  const clients = [{
    name: 'Client 1',
    logo: 'https://via.placeholder.com/150?text=Logo+1'
  }, {
    name: 'Client 2',
    logo: 'https://via.placeholder.com/150?text=Logo+2'
  }, {
    name: 'Client 3',
    logo: 'https://via.placeholder.com/150?text=Logo+3'
  }, {
    name: 'Client 4',
    logo: 'https://via.placeholder.com/150?text=Logo+4'
  }, {
    name: 'Client 5',
    logo: 'https://via.placeholder.com/150?text=Logo+5'
  }, {
    name: 'Client 6',
    logo: 'https://via.placeholder.com/150?text=Logo+6'
  }];
  const pricingPlans: PricingPlan[] = [{
    name: 'Starter',
    nameAr: 'المبتدئ',
    priceMonthly: pricing.starter.monthly,
    priceYearly: pricing.starter.yearly,
    features: ['Up to 5,000 Monthly Conversations', 'Limited AI Replies', 'Dashboard', 'Conversations Inbox', 'Limited Follow-up', 'Products & Services', 'Limited Offers Management', 'Limited AI Auto Replies', 'Basic Analytics & Reports', 'Standard Support'],
    featuresAr: ['حتى 5,000 محادثة شهرياً', 'ردود AI محدودة', 'لوحة التحكم', 'صندوق المحادثات', 'متابعة محدودة', 'المنتجات والخدمات', 'إدارة عروض محدودة', 'ردود تلقائية AI محدودة', 'تحليلات وتقارير أساسية', 'دعم قياسي']
  }, {
    name: 'Growth',
    nameAr: 'النمو',
    priceMonthly: pricing.business.monthly,
    priceYearly: pricing.business.yearly,
    features: ['Up to 10,000 Monthly Conversations', 'Standard AI Replies', 'Dashboard', 'Conversations Inbox', 'Full Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Standard Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 10,000 محادثة شهرياً', 'ردود AI قياسية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء كاملة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير قياسية', 'دعم ذو أولوية'],
    popular: true
  }, {
    name: 'Scale',
    nameAr: 'التوسع',
    priceMonthly: pricing.pro.monthly,
    priceYearly: pricing.pro.yearly,
    features: ['Up to 100,000 Monthly Conversations', 'High AI Replies', 'Dashboard', 'Conversations Inbox', 'Advanced Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Advanced Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 100,000 محادثة شهرياً', 'ردود AI عالية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء متقدمة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير متقدمة', 'دعم ذو أولوية']
  }];
  const stats = [{
    icon: MessageSquare,
    value: '+10K',
    label: isRTL ? 'رسالة يومياً' : 'Messages Daily',
    color: 'text-[#4f1964]'
  }, {
    icon: Users,
    value: '+500',
    label: isRTL ? 'عميل نشط' : 'Active Clients',
    color: 'text-[#4f1964]'
  }, {
    icon: TrendingUp,
    value: '99.9%',
    label: isRTL ? 'وقت التشغيل' : 'Uptime',
    color: 'text-[#4f1964]'
  }, {
    icon: Clock,
    value: '24/7',
    label: isRTL ? 'دعم متواصل' : 'Support',
    color: 'text-[#4f1964]'
  }];
  const faqs = [{
    q: isRTL ? 'هل أحتاج خبرة تقنية؟' : 'Do I need technical expertise?',
    a: isRTL ? 'لا، النظام سهل جداً. فقط اربط رقمك واكتب رسالة النظام وابدأ.' : 'No, the system is very easy. Just connect your number, write the system message and start.'
  }, {
    q: isRTL ? 'هل يمكن تغيير System Message؟' : 'Can I change the System Message?',
    a: isRTL ? 'نعم، يمكنك تعديلها في أي وقت من لوحة التحكم.' : 'Yes, you can edit it anytime from the dashboard.'
  }, {
    q: isRTL ? 'هل يمكن ربط أكثر من رقم؟' : 'Can I connect multiple numbers?',
    a: isRTL ? 'نعم، حسب الباقة المختارة.' : 'Yes, depending on your chosen plan.'
  }, {
    q: isRTL ? 'هل يدعم Google Sheets؟' : 'Does it support Google Sheets?',
    a: isRTL ? 'نعم، تكامل كامل مع Google Sheets.' : 'Yes, full integration with Google Sheets.'
  }, {
    q: isRTL ? 'هل أستطيع إيقاف/تشغيل البوت؟' : 'Can I stop/start the bot?',
    a: isRTL ? 'نعم، تحكم كامل من لوحة التحكم.' : 'Yes, full control from the dashboard.'
  }];
  return <div className="min-h-screen bg-white text-gray-900" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2353">
      {/* Announcement Banner */}
      <motion.div initial={{
      y: -50,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#4f1964] via-purple-600 to-pink-600 text-white overflow-hidden" data-id="element-2354">
        <div className="relative py-2 sm:py-2.5" data-id="element-2355">
          {/* Animated background shine */}
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12" animate={{
          x: ['-200%', '200%']
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 2
        }} data-id="element-2356" />

          {/* Scrolling content */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 px-4" data-id="element-2357">
            <motion.span animate={{
            rotate: [0, 15, -15, 0],
            scale: [1, 1.2, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="text-base sm:text-lg" data-id="element-2358">
              ✨
            </motion.span>
            <span className="text-xs sm:text-sm font-medium" data-id="element-2359">
              {isRTL ? 'جرب كل المميزات مجاناً لمدة 7 أيام' : 'Try all features free for 7 days'}
            </span>
            <Link to="/register" data-id="element-2360">
              <motion.span animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 hover:bg-white/30 rounded-full text-[10px] sm:text-xs font-bold transition-colors cursor-pointer" data-id="element-2361">
                {isRTL ? 'ابدأ الآن' : 'Start Now'}
              </motion.span>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Sticky Header */}
      <motion.header className="fixed top-10 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm" initial={{
      y: -100
    }} animate={{
      y: 0
    }} data-id="element-2362">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2363">
          <div className="flex items-center justify-between py-4" data-id="element-2364">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-2365" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-id="element-2366">
              <Link to="/" className="text-[#4f1964] font-medium transition-colors" data-id="element-2367">
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              <Link to="/about" className="text-gray-600 hover:text-[#4f1964] transition-colors font-medium" data-id="element-2368">
                {isRTL ? 'من نحن' : 'About Us'}
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#4f1964] transition-colors font-medium" data-id="element-2369">
                {isRTL ? 'المدونة' : 'Blog'}
              </Link>
              <a href="#features" className="text-gray-600 hover:text-[#4f1964] transition-colors font-medium" data-id="element-2370">
                {isRTL ? 'المميزات' : 'Features'}
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-[#4f1964] transition-colors font-medium" data-id="element-2371">
                {isRTL ? 'الأسعار' : 'Pricing'}
              </a>
              <a href="#faq" className="text-gray-600 hover:text-[#4f1964] transition-colors font-medium" data-id="element-2372">
                {isRTL ? 'الأسئلة' : 'FAQ'}
              </a>

              {/* Language Selector Dropdown */}
              <div className="relative" data-id="element-2373">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2374">
                  <Globe className="w-5 h-5 text-gray-600" data-id="element-2375" />
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2376" />
                </button>

                <AnimatePresence data-id="element-2377">
                  {languageDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[140px]" data-id="element-2378">
                      <button onClick={() => handleLanguageChange('en')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'en' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2379">
                        <span className="text-lg" data-id="element-2380">🇺🇸</span>
                        <span data-id="element-2381">English</span>
                        {language === 'en' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2382" />}
                      </button>
                      <button onClick={() => handleLanguageChange('ar')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2383">
                        <span className="text-lg" data-id="element-2384">🇸🇦</span>
                        <span data-id="element-2385">العربية</span>
                        {language === 'ar' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2386" />}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Country Selector Dropdown */}
              <div className="relative" data-id="element-2387">
                <button onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-2388">
                  <span className="text-lg" data-id="element-2389">
                    {country === 'eg' ? '🇪🇬' : country === 'ae' ? '🇦🇪' : '🇸🇦'}
                  </span>
                  <span data-id="element-2390">
                    {country === 'eg' ? isRTL ? 'مصر' : 'Egypt' : country === 'ae' ? isRTL ? 'الإمارات' : 'UAE' : isRTL ? 'السعودية' : 'Saudi'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2391" />
                </button>

                <AnimatePresence data-id="element-2392">
                  {countryDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[180px]" data-id="element-2393">
                      <button onClick={() => handleCountryChange('eg')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'eg' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2394">
                        <span className="text-2xl" data-id="element-2395">🇪🇬</span>
                        <div className="text-left" data-id="element-2396">
                          <div className="font-semibold" data-id="element-2397">
                            {isRTL ? 'مصر' : 'Egypt'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2398">EGP - ج.م</div>
                        </div>
                        {country === 'eg' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2399" />}
                      </button>
                      <button onClick={() => handleCountryChange('ae')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'ae' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2400">
                        <span className="text-2xl" data-id="element-2401">🇦🇪</span>
                        <div className="text-left" data-id="element-2402">
                          <div className="font-semibold" data-id="element-2403">
                            {isRTL ? 'الإمارات' : 'UAE'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2404">
                            AED - درهم
                          </div>
                        </div>
                        {country === 'ae' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2405" />}
                      </button>
                      <button onClick={() => handleCountryChange('sa')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'sa' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2406">
                        <span className="text-2xl" data-id="element-2407">🇸🇦</span>
                        <div className="text-left" data-id="element-2408">
                          <div className="font-semibold" data-id="element-2409">
                            {isRTL ? 'السعودية' : 'Saudi'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2410">SAR - ر.س</div>
                        </div>
                        {country === 'sa' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2411" />}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600 hover:text-[#4f1964]" data-id="element-2412">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-2413" /> : <Menu className="w-6 h-6" data-id="element-2414" />}
            </button>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center gap-3" data-id="element-2415">
              <Link to="/login" data-id="element-2416">
                <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-2417">
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Button>
              </Link>
              <Link to="/register" data-id="element-2418">
                <Button size="sm" className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-2419">
                  {isRTL ? 'ابدأ الآن' : 'Start Now'}
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence data-id="element-2420">
            {mobileMenuOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="md:hidden overflow-hidden border-t border-gray-200" data-id="element-2421">
                <div className="py-4 space-y-4" data-id="element-2422">
                  <a href="#features" className="block text-gray-600 hover:text-[#4f1964] font-medium" onClick={() => setMobileMenuOpen(false)} data-id="element-2423">
                    {isRTL ? 'المميزات' : 'Features'}
                  </a>
                  <Link to="/about" className="block text-gray-600 hover:text-[#4f1964] font-medium" onClick={() => setMobileMenuOpen(false)} data-id="element-2424">
                    {isRTL ? 'من نحن' : 'About Us'}
                  </Link>
                  <Link to="/blog" className="block text-gray-600 hover:text-[#4f1964] font-medium" onClick={() => setMobileMenuOpen(false)} data-id="element-2425">
                    {isRTL ? 'المدونة' : 'Blog'}
                  </Link>
                  <a href="#pricing" className="block text-gray-600 hover:text-[#4f1964] font-medium" onClick={() => setMobileMenuOpen(false)} data-id="element-2426">
                    {isRTL ? 'الأسعار' : 'Pricing'}
                  </a>
                  <a href="#faq" className="block text-gray-600 hover:text-[#4f1964] font-medium" onClick={() => setMobileMenuOpen(false)} data-id="element-2427">
                    {isRTL ? 'الأسئلة' : 'FAQ'}
                  </a>

                  {/* Language Selector Mobile */}
                  <div className="flex gap-2" data-id="element-2428">
                    <button onClick={() => {
                  handleLanguageChange('en');
                  setMobileMenuOpen(false);
                }} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${language === 'en' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964]' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2429">
                      <span className="text-lg" data-id="element-2430">🇺🇸</span>
                      <span data-id="element-2431">English</span>
                    </button>
                    <button onClick={() => {
                  handleLanguageChange('ar');
                  setMobileMenuOpen(false);
                }} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${language === 'ar' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964]' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2432">
                      <span className="text-lg" data-id="element-2433">🇸🇦</span>
                      <span data-id="element-2434">العربية</span>
                    </button>
                  </div>

                  {/* Country Selector Mobile */}
                  <div className="flex gap-2" data-id="element-2435">
                    <button onClick={() => {
                  handleCountryChange('eg');
                  setMobileMenuOpen(false);
                }} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${country === 'eg' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964]' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2436">
                      <span className="text-lg" data-id="element-2437">🇪🇬</span>
                      <span data-id="element-2438">{isRTL ? 'مصر' : 'Egypt'}</span>
                    </button>
                    <button onClick={() => {
                  handleCountryChange('ae');
                  setMobileMenuOpen(false);
                }} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${country === 'ae' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964]' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2439">
                      <span className="text-lg" data-id="element-2440">🇦🇪</span>
                      <span data-id="element-2441">{isRTL ? 'الإمارات' : 'UAE'}</span>
                    </button>
                    <button onClick={() => {
                  handleCountryChange('sa');
                  setMobileMenuOpen(false);
                }} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${country === 'sa' ? 'border-[#4f1964] bg-purple-50 text-[#4f1964]' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2442">
                      <span className="text-lg" data-id="element-2443">🇸🇦</span>
                      <span data-id="element-2444">{isRTL ? 'السعودية' : 'Saudi'}</span>
                    </button>
                  </div>

                  <div className="flex flex-col gap-2 pt-2" data-id="element-2445">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)} data-id="element-2446">
                      <Button variant="outline" size="sm" fullWidth className="border-gray-300 text-gray-700" data-id="element-2447">
                        {isRTL ? 'تسجيل الدخول' : 'Login'}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)} data-id="element-2448">
                      <Button size="sm" fullWidth className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-2449">
                        {isRTL ? 'ابدأ الآن' : 'Start Now'}
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 bg-gradient-to-b from-gray-50 to-white" data-id="element-2450">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2451">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center" data-id="element-2452">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-4" data-id="element-2453">
              <span className="relative flex h-2 w-2" data-id="element-2454">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4f1964] opacity-75" data-id="element-2455"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4f1964]" data-id="element-2456"></span>
              </span>
              {isRTL ? 'الجيل الجديد من الأتمتة' : 'Next Generation Automation'}
            </motion.div>

            {/* Free Trial Badge - Animated */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="relative inline-block mb-8" data-id="element-2457">
              {/* Glowing background effect */}
              <motion.div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full blur-xl opacity-50" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2458" />

              {/* Main badge */}
              <motion.div className="relative flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-base font-bold shadow-lg overflow-hidden" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} data-id="element-2459">
                {/* Shine effect */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12" animate={{
                x: ['-200%', '200%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                repeatDelay: 1
              }} data-id="element-2460" />

                {/* Sparkle icon with animation */}
                <motion.span className="text-2xl relative z-10" animate={{
                rotate: [0, 15, -15, 0],
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-2461">
                  ✨
                </motion.span>

                <span className="relative z-10" data-id="element-2462">
                  {isRTL ? 'تجربة مجانية لمدة 7 أيام' : '7 Days Free Trial'}
                </span>

                {/* Floating particles */}
                <motion.span className="absolute text-xs" style={{
                top: '20%',
                right: '10%'
              }} animate={{
                y: [0, -10, 0],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: 0
              }} data-id="element-2463">
                  ⭐
                </motion.span>
                <motion.span className="absolute text-xs" style={{
                bottom: '20%',
                left: '15%'
              }} animate={{
                y: [0, -8, 0],
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: 0.5
              }} data-id="element-2464">
                  💫
                </motion.span>
              </motion.div>
            </motion.div>

            {/* Main Heading */}
            <motion.h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-gray-900 leading-tight px-4" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} data-id="element-2465">
              {isRTL ? <>
                  حوّل{' '}
                  <motion.span className="text-[#4f1964] inline-block" animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }} transition={{
                duration: 5,
                repeat: Infinity
              }} style={{
                backgroundImage: 'linear-gradient(90deg, #4f1964, #7c3aed, #4f1964)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }} data-id="element-2466">
                    WhatsApp
                  </motion.span>{' '}
                  إلى آلة مبيعات تعمل 24/7 بالذكاء الاصطناعي
                </> : <>
                  Turn your{' '}
                  <motion.span className="text-[#4f1964] inline-block" animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }} transition={{
                duration: 5,
                repeat: Infinity
              }} style={{
                backgroundImage: 'linear-gradient(90deg, #4f1964, #7c3aed, #4f1964)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }} data-id="element-2467">
                    WhatsApp
                  </motion.span>{' '}
                  into a 24/7 sales machine powered by AI
                </>}
            </motion.h1>

            {/* Subtitle */}
            <motion.p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed px-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.6
          }} data-id="element-2468">
              {isRTL ? 'حوّل واتساب إلى آلة مبيعات تعمل على مدار الساعة بالذكاء الاصطناعي — أتمتة المحادثات، إدارة المنتجات والعروض، ولا تفوّت أي عميل.' : 'Automate conversations, manage products & offers, and never miss a lead again.'}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 px-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} data-id="element-2469">
              <Link to="/register" className="w-full sm:w-auto" data-id="element-2470">
                <motion.div whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.98
              }} data-id="element-2471">
                  <Button size="lg" className="bg-[#4f1964] hover:bg-[#3d1350] text-white shadow-lg w-full sm:min-w-[200px]" data-id="element-2472">
                    {isRTL ? 'ابدأ الآن مجاناً' : 'Start Now Free'}
                  </Button>
                </motion.div>
              </Link>
              <Link to="/try-demo" className="w-full sm:w-auto" data-id="element-2473">
                <motion.div whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.98
              }} data-id="element-2474">
                  <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:min-w-[200px]" data-id="element-2475">
                    {isRTL ? 'جرب الديمو' : 'Try Demo'}
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            {/* Stats Grid */}
            <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto px-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-2476">
              {stats.map((stat, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              delay: 0.5 + i * 0.1,
              type: 'spring',
              stiffness: 200
            }} whileHover={{
              y: -5,
              scale: 1.05,
              boxShadow: '0 20px 40px rgba(79, 25, 100, 0.15)'
            }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200 transition-all cursor-pointer group" data-id="element-2477">
                  <motion.div whileHover={{
                rotate: [0, -10, 10, 0]
              }} transition={{
                duration: 0.5
              }} data-id="element-2478">
                    <stat.icon className={`w-6 h-6 sm:w-8 sm:h-8 ${stat.color} mx-auto mb-2 sm:mb-3 group-hover:scale-110 transition-transform`} data-id="element-2479" />
                  </motion.div>
                  <motion.div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.7 + i * 0.1
              }} data-id="element-2480">
                    {stat.value}
                  </motion.div>
                  <div className="text-xs sm:text-sm text-gray-500" data-id="element-2481">
                    {stat.label}
                  </div>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Clients Marquee Section (under Hero) ── */}
      <BrandsMarquee data-id="element-2482" />

      {/* How It Works - Quick Preview */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-white relative overflow-hidden" data-id="element-2483">
        {/* Animated background decoration */}
        <motion.div className="absolute top-0 left-0 w-full h-full pointer-events-none" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-2484">
          <motion.div animate={{
          x: [0, 30, 0],
          y: [0, -20, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-10 w-32 h-32 bg-purple-100 rounded-full blur-3xl opacity-40" data-id="element-2485" />
          <motion.div animate={{
          x: [0, -20, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute bottom-20 right-10 w-40 h-40 bg-pink-100 rounded-full blur-3xl opacity-40" data-id="element-2486" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2487">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12 sm:mb-16" data-id="element-2488">
            <motion.span initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-4" data-id="element-2489">
              <Sparkles className="w-4 h-4" data-id="element-2490" />
              {isRTL ? 'سهل وسريع' : 'Easy & Fast'}
            </motion.span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900" data-id="element-2491">
              {isRTL ? 'كيف يعمل؟' : 'How It Works?'}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mb-6" data-id="element-2492">
              {isRTL ? '3 خطوات بسيطة للبدء' : '3 Simple Steps to Get Started'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-10 relative" data-id="element-2493">
            {/* Connecting line between steps */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 -translate-y-1/2 z-0" data-id="element-2494">
              <motion.div initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1.2,
              delay: 0.5,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-purple-200 via-purple-300 to-purple-200 origin-left mx-20" data-id="element-2495" />
            </div>

            {[{
            step: '1',
            title: isRTL ? 'أنشئ حسابك' : 'Create Your Account',
            desc: isRTL ? 'سجل واكتب اسم البراند الخاص بك' : 'Register and write your brand name',
            icon: Sparkles,
            color: 'from-purple-600 to-pink-600'
          }, {
            step: '2',
            title: isRTL ? 'اربط واتساب بالـ QR' : 'Connect WhatsApp via QR',
            desc: isRTL ? 'امسح الكود لربط رقمك في ثوانٍ' : 'Scan code to connect your number in seconds',
            icon: MessageSquare,
            color: 'from-blue-600 to-purple-600'
          }, {
            step: '3',
            title: isRTL ? 'جهّز البوت وابدأ' : 'Setup Bot & Launch',
            desc: isRTL ? 'اكتب System Message، أضف منتجاتك وعروضك وأسئلتك، وفعّل البوت' : 'Write System Message, add products, offers & Q&A, then activate',
            icon: Bot,
            color: 'from-green-600 to-blue-600'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            y: 0,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.25,
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} whileHover={{
            y: -8,
            scale: 1.02
          }} onClick={() => navigate('/register')} className="relative p-6 sm:p-8 rounded-2xl bg-white border-2 border-gray-200 hover:border-[#4f1964] hover:shadow-xl transition-all cursor-pointer group z-10" data-id="element-2496">
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} whileInView={{
              scale: 1,
              rotate: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3 + i * 0.25,
              type: 'spring',
              stiffness: 300
            }} className="absolute top-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold text-[#4f1964]" data-id="element-2497">
                  {item.step}
                </motion.div>
                <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform shadow-lg`} data-id="element-2498">
                  <item.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" data-id="element-2499" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-gray-900" data-id="element-2500">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600" data-id="element-2501">
                  {item.desc}
                </p>
                <motion.div initial={{
              opacity: 0,
              x: -10
            }} whileHover={{
              opacity: 1,
              x: 0
            }} className="mt-4 text-[#4f1964] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2502">
                  {isRTL ? 'ابدأ الآن ←' : '→ Start Now'}
                </motion.div>
              </motion.div>)}
          </div>

          {/* Learn More Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-2503">
            <Link to="/how-it-works" data-id="element-2504">
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.98
            }} data-id="element-2505">
                <Button variant="outline" size="lg" className="border-[#4f1964] text-[#4f1964] hover:bg-purple-50" data-id="element-2506">
                  <Play className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-2507" />
                  {isRTL ? 'شاهد الشرح المفصل' : 'Watch Detailed Tutorial'}
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden" data-id="element-2508">
        {/* Animated background */}
        <motion.div className="absolute inset-0 pointer-events-none" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-2509">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 60,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -top-40 -right-40 w-80 h-80 border border-purple-100 rounded-full opacity-30" data-id="element-2510" />
          <motion.div animate={{
          rotate: -360
        }} transition={{
          duration: 45,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -bottom-20 -left-20 w-60 h-60 border border-pink-100 rounded-full opacity-30" data-id="element-2511" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2512">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12 sm:mb-16" data-id="element-2513">
            <motion.span initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-4" data-id="element-2514">
              <Zap className="w-4 h-4" data-id="element-2515" />
              {isRTL ? 'كل ما تحتاجه' : 'All You Need'}
            </motion.span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900" data-id="element-2516">
              {isRTL ? 'مميزات لوحة التحكم' : 'Dashboard Features'}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto" data-id="element-2517">
              {isRTL ? 'لوحة تحكم شاملة لإدارة كل جوانب عملك عبر واتساب' : 'A comprehensive dashboard to manage every aspect of your WhatsApp business'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5" data-id="element-2518">
            {[{
            icon: Bot,
            title: isRTL ? 'مساعد AI ذكي' : 'Smart AI Agent',
            desc: isRTL ? 'ردود ذكية تلقائية 24/7' : 'Auto replies 24/7',
            gradient: 'from-purple-500 to-indigo-600',
            delay: 0
          }, {
            icon: ShoppingBag,
            title: isRTL ? 'إدارة المنتجات' : 'Product Management',
            desc: isRTL ? 'منتجات مع روابط اختيارية' : 'Products with optional links',
            gradient: 'from-blue-500 to-cyan-600',
            delay: 0.05
          }, {
            icon: Tag,
            title: isRTL ? 'العروض والخصومات' : 'Offers & Discounts',
            desc: isRTL ? 'عروض مع تواريخ وروابط' : 'Offers with dates & links',
            gradient: 'from-orange-500 to-red-500',
            delay: 0.1
          }, {
            icon: HelpCircle,
            title: isRTL ? 'قاعدة المعرفة' : 'Q&A Knowledge Base',
            desc: isRTL ? 'درّب البوت بأسئلة وأجوبة' : 'Train bot with Q&A',
            gradient: 'from-emerald-500 to-teal-600',
            delay: 0.15
          }, {
            icon: Users,
            title: isRTL ? 'إدارة العملاء' : 'Customer CRM',
            desc: isRTL ? 'استيراد وتصدير CSV' : 'Import/export CSV',
            gradient: 'from-pink-500 to-rose-600',
            delay: 0.2
          }, {
            icon: MessageCircle,
            title: isRTL ? 'محادثات مباشرة' : 'Live Conversations',
            desc: isRTL ? 'عرض والرد على الرسائل' : 'View & reply to messages',
            gradient: 'from-green-500 to-emerald-600',
            delay: 0.25
          }, {
            icon: Power,
            title: isRTL ? 'تحكم بالبوت' : 'Bot Control',
            desc: isRTL ? 'تشغيل/إيقاف بضغطة زر' : 'Enable/disable in one click',
            gradient: 'from-violet-500 to-purple-600',
            delay: 0.3
          }, {
            icon: ShoppingBag,
            title: isRTL ? 'شوبيفاي' : 'Shopify',
            desc: isRTL ? 'تكامل مع شوبيفاي (تجريبي)' : 'Shopify integration (Beta)',
            gradient: 'from-green-500 to-emerald-600',
            delay: 0.35
          }, {
            icon: BarChart3,
            title: isRTL ? 'تحليلات متقدمة' : 'Advanced Analytics',
            desc: isRTL ? 'رسوم بيانية وتقارير' : 'Charts & reports',
            gradient: 'from-amber-500 to-orange-600',
            delay: 0.4
          }, {
            icon: Languages,
            title: isRTL ? 'عربي وإنجليزي' : 'Arabic & English',
            desc: isRTL ? 'دعم كامل للغتين' : 'Full bilingual support',
            gradient: 'from-sky-500 to-blue-600',
            delay: 0.45
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 25,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            y: 0,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: feature.delay,
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} whileHover={{
            y: -6,
            boxShadow: '0 20px 40px rgba(79, 25, 100, 0.12)'
          }} onClick={() => navigate('/register')} className="p-4 sm:p-5 rounded-xl bg-white border border-gray-200 hover:border-[#4f1964]/30 transition-all cursor-pointer group" data-id="element-2519">
                <motion.div whileHover={{
              rotate: [0, -8, 8, 0],
              scale: 1.1
            }} transition={{
              duration: 0.4
            }} className={`w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br ${feature.gradient} rounded-lg flex items-center justify-center mb-3 shadow-sm`} data-id="element-2520">
                  <feature.icon className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-white" data-id="element-2521" />
                </motion.div>
                <h3 className="font-bold mb-1 text-sm sm:text-[15px] text-gray-900 leading-tight" data-id="element-2522">
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-[13px] text-gray-500 leading-relaxed" data-id="element-2523">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-24 bg-gray-50" data-id="element-2524">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2525">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8 sm:mb-12" data-id="element-2526">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900" data-id="element-2527">
              {isRTL ? 'الأسعار' : 'Pricing'}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mb-6" data-id="element-2528">
              {isRTL ? 'اختر الباقة المناسبة لك' : 'Choose the plan that fits you'}
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-1 border-2 border-gray-200 inline-flex mb-4" data-id="element-2529">
              <button onClick={() => setBillingPeriod('monthly')} className={`px-4 sm:px-6 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-[#4f1964] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2530">
                {isRTL ? 'شهري' : 'Monthly'}
              </button>
              <button onClick={() => setBillingPeriod('yearly')} className={`px-4 sm:px-6 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'yearly' ? 'bg-[#4f1964] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2531">
                {isRTL ? 'سنوي' : 'Yearly'}
              </button>
            </div>

            {billingPeriod === 'yearly' && <div className="inline-block px-4 py-2 bg-green-100 border border-green-200 rounded-full text-green-700 text-sm font-medium" data-id="element-2532">
                ✨{' '}
                {isRTL ? 'وفر أكثر مع الباقة السنوية' : 'Save more with yearly plan'}
              </div>}
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 sm:gap-8" data-id="element-2533">
            {pricingPlans.map((plan, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative p-6 sm:p-8 rounded-2xl border-2 transition-all bg-white ${plan.popular ? 'border-[#4f1964] shadow-xl scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'}`} data-id="element-2534">
                {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#4f1964] text-white rounded-full text-sm font-bold" data-id="element-2535">
                    {isRTL ? 'الأكثر شعبية' : 'Most Popular'}
                  </div>}

                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900" data-id="element-2536">
                  {isRTL ? plan.nameAr : plan.name}
                </h3>
                <div className="mb-6" data-id="element-2537">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2538">
                    <span className="text-sm text-gray-400 line-through" data-id="element-2539">
                      {(billingPeriod === 'monthly' ? plan.priceMonthly * 2 : plan.priceYearly * 2).toLocaleString()}{' '}
                      {currencySymbol}
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full" data-id="element-2540">
                      -50%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-2541">
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-2542">
                      {billingPeriod === 'monthly' ? plan.priceMonthly.toLocaleString() : plan.priceYearly.toLocaleString()}
                    </span>
                    <span className="text-gray-600" data-id="element-2543">{currencySymbol}</span>
                    <span className="text-gray-500 text-sm" data-id="element-2544">
                      /{' '}
                      {billingPeriod === 'monthly' ? isRTL ? 'شهر' : 'month' : isRTL ? 'سنة' : 'year'}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8" data-id="element-2545">
                  {(isRTL ? plan.featuresAr : plan.features).map((feature, j) => <li key={j} className="flex items-center gap-2" data-id="element-2546">
                        <CheckCircle2 className="w-5 h-5 text-[#4f1964] flex-shrink-0" data-id="element-2547" />
                        <span className="text-sm sm:text-base text-gray-700" data-id="element-2548">
                          {feature}
                        </span>
                      </li>)}
                </ul>

                <Link to="/register" data-id="element-2549">
                  <Button fullWidth variant={plan.popular ? 'primary' : 'outline'} className={plan.popular ? 'bg-[#4f1964] hover:bg-[#3d1350]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} data-id="element-2550">
                    {isRTL ? 'ابدأ الآن' : 'Start Now'}
                  </Button>
                </Link>
              </motion.div>)}

            {/* Custom Plan */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="relative p-6 sm:p-8 rounded-2xl border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 hover:border-[#4f1964] hover:shadow-lg transition-all" data-id="element-2551">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-bold" data-id="element-2552">
                {isRTL ? 'للشركات' : 'Enterprise'}
              </div>

              <div className="flex items-center gap-3 mb-4" data-id="element-2553">
                <Building2 className="w-8 h-8 text-[#4f1964]" data-id="element-2554" />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2555">
                  {isRTL ? 'خطة مخصصة' : 'Custom Plan'}
                </h3>
              </div>

              <p className="text-gray-600 mb-6" data-id="element-2556">
                {isRTL ? 'حلول مخصصة للشركات الكبيرة مع دعم مخصص وتكاملات متقدمة' : 'Custom solutions for large enterprises with dedicated support and advanced integrations'}
              </p>

              <ul className="space-y-3 mb-8" data-id="element-2557">
                {[isRTL ? 'محادثات شهرية مخصصة' : 'Custom Monthly Conversations', isRTL ? 'ردود AI مخصصة' : 'Custom AI Replies', isRTL ? 'إدارة عملاء مخصصة' : 'Custom Leads Management', isRTL ? 'تحليلات وتقارير مخصصة' : 'Custom Analytics & Reports', isRTL ? 'دعم مخصص' : 'Dedicated Support', isRTL ? 'مدير حساب مخصص' : 'Dedicated Account Manager'].map((feature, j) => <li key={j} className="flex items-center gap-2" data-id="element-2558">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" data-id="element-2559" />
                    <span className="text-sm sm:text-base text-gray-700" data-id="element-2560">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <a href={`https://wa.me/${country === 'eg' ? '201508087501' : '971508546970'}`} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2561">
                <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 w-full" data-id="element-2562">
                  <Phone className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-2563" />
                  {isRTL ? 'تواصل واتساب' : 'Contact WhatsApp'}
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-24 bg-white" data-id="element-2564">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2565">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8 sm:mb-12" data-id="element-2566">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900" data-id="element-2567">
              {isRTL ? 'الأسئلة الشائعة' : 'FAQ'}
            </h2>
          </motion.div>

          <div className="space-y-4" data-id="element-2568">
            {faqs.map((faq, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-[#4f1964] transition-colors" data-id="element-2569">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 sm:p-6 text-left hover:bg-gray-50 transition-colors" data-id="element-2570">
                  <span className="font-semibold text-sm sm:text-base text-gray-900 pr-4" data-id="element-2571">
                    {faq.q}
                  </span>
                  {openFaq === i ? <ChevronUp className="text-[#4f1964] flex-shrink-0" data-id="element-2572" /> : <ChevronDown className="text-gray-400 flex-shrink-0" data-id="element-2573" />}
                </button>
                <AnimatePresence data-id="element-2574">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2575">
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-sm sm:text-base text-gray-600" data-id="element-2576">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-purple-50 to-pink-50" data-id="element-2577">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2578">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-2579">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900" data-id="element-2580">
              {isRTL ? 'ابدأ خلال 10 دقائق' : 'Start in 10 Minutes'}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-8" data-id="element-2581">
              {isRTL ? 'لا تحتاج خبرة تقنية. فقط اربط رقمك وابدأ' : 'No technical expertise needed. Just connect and start'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2582">
              <Link to="/register" className="w-full sm:w-auto" data-id="element-2583">
                <Button size="lg" className="bg-[#4f1964] hover:bg-[#3d1350] text-white shadow-lg w-full" data-id="element-2584">
                  {isRTL ? 'إنشاء حساب' : 'Create Account'}
                </Button>
              </Link>
              <a href="https://wa.me/201508087501" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2585">
                <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 w-full" data-id="element-2586">
                  <Phone className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-2587" />
                  {isRTL ? 'تواصل واتساب' : 'Contact WhatsApp'}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4f1964] text-white py-8 sm:py-12" data-id="element-2588">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2589">
          <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-2590">
            <div data-id="element-2591">
              <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="text-2xl font-bold text-white mb-4 block" data-id="element-2592">
                operines
              </motion.span>
              <p className="text-purple-200 text-sm" data-id="element-2593">
                {isRTL ? 'أتمتة واتساب بالذكاء الاصطناعي' : 'AI-Powered WhatsApp Automation'}
              </p>
            </div>

            <div data-id="element-2594">
              <h4 className="font-bold mb-4" data-id="element-2595">
                {isRTL ? 'روابط سريعة' : 'Quick Links'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-2596">
                <li data-id="element-2597">
                  <Link to="/" className="hover:text-white transition-colors" data-id="element-2598">
                    {isRTL ? 'الرئيسية' : 'Home'}
                  </Link>
                </li>
                <li data-id="element-2599">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-2600">
                    {isRTL ? 'من نحن' : 'About Us'}
                  </Link>
                </li>
                <li data-id="element-2601">
                  <Link to="/blog" className="hover:text-white transition-colors" data-id="element-2602">
                    {isRTL ? 'المدونة' : 'Blog'}
                  </Link>
                </li>
                <li data-id="element-2603">
                  <a href="#features" className="hover:text-white transition-colors" data-id="element-2604">
                    {isRTL ? 'المميزات' : 'Features'}
                  </a>
                </li>
                <li data-id="element-2605">
                  <a href="#pricing" className="hover:text-white transition-colors" data-id="element-2606">
                    {isRTL ? 'الأسعار' : 'Pricing'}
                  </a>
                </li>
                <li data-id="element-2607">
                  <a href="#faq" className="hover:text-white transition-colors" data-id="element-2608">
                    {isRTL ? 'الأسئلة' : 'FAQ'}
                  </a>
                </li>
                <li data-id="element-2609">
                  <Link to="/privacy" className="hover:text-white transition-colors" data-id="element-2610">
                    {isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}
                  </Link>
                </li>
                <li data-id="element-2611">
                  <Link to="/terms" className="hover:text-white transition-colors" data-id="element-2612">
                    {isRTL ? 'شروط الخدمة' : 'Terms of Service'}
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-2613">
              <h4 className="font-bold mb-4" data-id="element-2614">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-2615">
                <li data-id="element-2616">hello@operines.ai</li>
                <li dir="ltr" data-id="element-2617">{phoneNumber}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-purple-800 pt-8 text-center text-purple-200 text-sm" data-id="element-2618">
            {/* Payment Methods - Stripe Only */}
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-2619">
              <span className="text-purple-400 text-xs" data-id="element-2620">
                {isRTL ? 'طرق الدفع' : 'Payment Methods'}
              </span>
              <div className="flex items-center gap-2" data-id="element-2621">
                {/* Visa */}
                <div className="bg-white rounded px-1.5 py-0.5 flex items-center justify-center h-7" data-id="element-2622">
                  <svg viewBox="0 0 48 16" className="h-4 w-auto" fill="none" data-id="element-2623">
                    <path d="M17.4 1.2l-3.2 13.6h-2.6l3.2-13.6h2.6zm13.2 8.8l1.4-3.8.8 3.8h-2.2zm2.9 4.8h2.4l-2.1-13.6h-2.2c-.5 0-.9.3-1.1.7l-3.8 12.9h2.7l.5-1.4h3.3l.3 1.4zm-6.6-4.4c0-3.6-5-3.8-5-5.4 0-.5.5-1 1.5-1.1.5-.1 1.9-.1 3.5.6l.6-2.9c-.9-.3-2-.6-3.3-.6-3.5 0-6 1.9-6 4.5 0 2 1.8 3.1 3.1 3.7 1.4.7 1.9 1.1 1.9 1.7 0 .9-1.1 1.3-2.2 1.4-1.8 0-2.9-.5-3.7-.9l-.7 3c.8.4 2.4.7 4 .7 3.7 0 6.1-1.8 6.3-4.7zM12.8 1.2L8.3 14.8H5.5L3.3 3.5c-.1-.5-.3-.7-.7-.9C1.8 2.2.6 1.8 0 1.5l.1-.3h4.3c.5 0 1 .4 1.1 1l1.1 5.7 2.6-6.7h2.6z" fill="#1A1F71" data-id="element-2624" />
                  </svg>
                </div>
                {/* Mastercard */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-7" data-id="element-2625">
                  <svg viewBox="0 0 32 20" className="h-4 w-auto" fill="none" data-id="element-2626">
                    <circle cx="12" cy="10" r="8" fill="#EB001B" data-id="element-2627" />
                    <circle cx="20" cy="10" r="8" fill="#F79E1B" data-id="element-2628" />
                    <path d="M16 3.8a8 8 0 010 12.4 8 8 0 000-12.4z" fill="#FF5F00" data-id="element-2629" />
                  </svg>
                </div>
              </div>
              <span className="text-purple-500 text-[10px]" data-id="element-2630">
                Powered by{' '}
                <span className="font-bold text-purple-300" data-id="element-2631">Stripe</span>
              </span>
            </div>
            © {new Date().getFullYear()} Operines.{' '}
            {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </div>
        </div>
      </footer>
    </div>;
}